import { Injectable } from '@angular/core';

export type AppVersionValue =
  | string
  | { major?: number; minor?: number; patch?: number }
  | null
  | undefined;

@Injectable({
  providedIn: 'root',
})
export class WebVersionEnforcerService {
  async enforceLatestVersionFromFile(
    versionFile: { version?: AppVersionValue } | null | undefined,
    appKey: string
  ) {
    return this.enforceLatestVersion(versionFile?.version, appKey);
  }

  async enforceLatestVersion(
    localVersionValue: AppVersionValue,
    appKey: string
  ) {
    if (typeof window === 'undefined' || !appKey) return;

    const localVersion = this.normalizeVersion(localVersionValue);
    if (!localVersion) return;

    try {
      const serverVersion = await this.fetchServerVersion();
      const storageKey = this.getStorageKey(appKey);

      if (!serverVersion || serverVersion === localVersion) {
        sessionStorage.removeItem(storageKey);
        return;
      }

      if (sessionStorage.getItem(storageKey) === serverVersion) {
        return;
      }

      sessionStorage.setItem(storageKey, serverVersion);
      window.location.replace(this.withCacheBuster(window.location.href));
    } catch (error) {
      console.warn(
        '[WebVersionEnforcerService] Unable to validate application version.',
        error
      );
    }
  }

  private async fetchServerVersion(): Promise<string | null> {
    const response = await fetch(`/version.json?v=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;

    const payload = (await response.json()) as { version?: AppVersionValue };
    return this.normalizeVersion(payload?.version);
  }

  private normalizeVersion(rawVersion: AppVersionValue) {
    if (!rawVersion) return null;

    if (typeof rawVersion === 'string') {
      const version = rawVersion.trim();
      return version.length > 0 ? version : null;
    }

    const { major, minor, patch } = rawVersion;
    if (major === undefined || minor === undefined || patch === undefined) {
      return null;
    }

    return `${major}.${minor}.${patch}`;
  }

  private withCacheBuster(urlValue: string) {
    const url = new URL(urlValue);
    url.searchParams.set('v', `${Date.now()}`);
    return url.toString();
  }

  private getStorageKey(appKey: string) {
    return `web-version-reload-target:${appKey}`;
  }
}
